import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import OrderedSelection from "../components/OrderedSelection";
import {
  foyerApi,
  type RoomTypeRecord,
  type SubPageRow,
  type ScreenCategory,
  SCREEN_CATEGORIES,
  SCREEN_CATEGORY_LABELS,
} from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { useCan } from "../auth/AuthContext";

/**
 * Admin-only catalogue of Room Types. Used by the per-premise
 * RoomsEditor as a dropdown when adding rooms. Non-admin users can
 * still GET the list (read-only) so the dropdown works, but only
 * admins reach this page (nav item is hidden) and the mutation
 * endpoints reject anyone else with 403.
 */
export default function RoomTypesPage() {
  // Gate on the capability, not the role - the server now enforces
  // `canManageRoomTypes` rather than a hand-rolled admin check, and the UI must
  // agree with it or the affordances drift from what the API actually allows.
  const isAdmin = useCan("canManageRoomTypes");
  const [items, setItems] = useState<RoomTypeRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: RoomTypeRecord } | null
  >(null);
  /**
   * The product catalogue, held at PAGE level rather than inside the dialog.
   *
   * Both the table and the editor need it — the table to name what each type
   * grants, the editor to offer the choices — and fetching it once here means
   * the names are already on screen when a save closes the dialog, instead of
   * appearing a moment later as a second request lands.
   */
  // Distinguished from "there are none": the first is a permission the operator
  // does not hold, the second is a catalogue nobody has imported yet, and they
  // want different sentences.

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listRoomTypes();
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    // Best-effort: reading it needs `canManageDistribution`, which a customer
    // holding `canManageRoomTypes` does not have. A 403 leaves the ids on
    // screen unresolved rather than breaking the page.
  }, []);

  async function onDelete(row: RoomTypeRecord) {
    const msg =
      row.roomCount > 0
        ? `Delete "${row.name}"? ${row.roomCount} room${
            row.roomCount === 1 ? "" : "s"
          } currently use this type; they'll be set to "Untyped".`
        : `Delete "${row.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await foyerApi.deleteRoomType(row.id);
      await refresh();
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Catalogue"
        title="Room types"
        actions={
          isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "create" })}
              data-testid="room-type-add"
            >
              New room type
            </Button>
          )
        }
      />

      {err && (
        <Typography variant="body2" color="error">
          {err}
        </Typography>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" stickyHeader data-testid="room-types-list">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <Tooltip title="Which licence pool this type's rooms draw from - Hotel/Care, Common area or Public viewing.">
                  <TableCell>Category</TableCell>
                </Tooltip>
                <TableCell align="right">Rooms using</TableCell>
                {isAdmin && <TableCell sx={{ width: 96 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  data-testid={`room-type-row-${row.id}`}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.description ?? ""}
                    </Typography>
                  </TableCell>
                  <TableCell data-testid={`room-type-category-${row.id}`}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        SCREEN_CATEGORY_LABELS[
                          row.category as ScreenCategory
                        ] ?? row.category
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={row.roomCount}
                    />
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${row.name}`}
                          onClick={() =>
                            setDialog({ mode: "edit", record: row })
                          }
                          data-testid={`room-type-edit-${row.id}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => onDelete(row)}
                          data-testid={`room-type-delete-${row.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 6 : 5}>
                    <Typography variant="caption" color="text.secondary">
                      No room types yet. Click <strong>New room type</strong> to
                      add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dialog && (
        <RoomTypeDialog
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
        />
      )}
    </PageContainer>
  );
}

function RoomTypeDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: RoomTypeRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /** Fetched once by the page — the table needs it too. */
}) {
  // Seeded once from `initial`. The caller mounts this dialog only when it has
  // a record, so the mount IS the seed — see [[cms-list-and-editor]].
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState(initial?.category ?? "room");
  const [pages, setPages] = useState<string[]>(() => initial?.pages ?? []);
  const [pageOptions, setPageOptions] = useState<SubPageRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // Best-effort: a customer holding `canManageRoomTypes` but not
    // `canManageDistribution` gets a 403 and simply edits ids by hand.
    void foyerApi
      .listSubPages()
      .then((r) => setPageOptions(r.subPages))
      .catch(() => setPageOptions([]));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const desc = description.trim() || null;
      if (initial) {
        await foyerApi.updateRoomType(initial.id, {
          name,
          description: desc,
          category,
          pages,
          // The COMPLETE list, never a delta. `syncRoomEntitlement` replaces
          // rather than merges, so a partial list here is a silent revoke.
        });
      } else {
        await foyerApi.createRoomType({ name, description: desc, category });
      }
      await onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? "Edit room type" : "New room type"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            autoFocus
            placeholder="Suite"
            inputProps={{ "data-testid": "room-type-name" }}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            multiline
            minRows={2}
            placeholder="Two-room suite with view"
          />
          {/* Upstream's `RoomTypeCategory`. The difference is real: a lobby
              screen has no guest, no purchase affordance and no continue-
              watching, so it genuinely shows a different set of rails.
              🔗 Also THE CONNECTOR (2026-09-07): this is what tells a
              licence's screen category (Hotel/Care / Common area / Public
              viewing - same three values, same labels) which rooms of this
              type actually draw against it. A hotel with no need for named
              types just creates three, one per category; a care facility
              keeps its named wings (Memory Care, Assisted Living, …) for
              call-contact routing (`callDirectory.ts`) and gives each the
              right category here. */}
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value as ScreenCategory)}
            size="small"
            helperText="Which licence pool this type's rooms draw from."
          >
            {SCREEN_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {SCREEN_CATEGORY_LABELS[c]}
              </MenuItem>
            ))}
            {!(SCREEN_CATEGORIES as readonly string[]).includes(category) && (
              <MenuItem value={category}>{category}</MenuItem>
            )}
          </TextField>
          {initial && (
            <>
              <OrderedSelection
                label="Sub-pages"
                hint="The extra pages this type offers, in menu order. A draft page never reaches a screen."
                value={pages}
                options={pageOptions.map((p) => ({
                  id: p.id,
                  label: p.name || p.id,
                }))}
                onChange={setPages}
                testId="room-type-pages"
              />
              {/* 🚨 The Products field is GONE, not hidden.
                  A room type decided what its rooms could PLAY until Sigge
                  ruled it out: a room type carries LAYOUT, so a content axis
                  multiplies it, and a room type is company-scoped or GLOBAL, so
                  content on a shared "Standard" leaked into customers who never
                  bought it. Entitlement follows the PREMISE LICENCE now.
                  Leaving the control here would have been worse than useless —
                  it wrote to a field nothing reads, so it looked like it worked
                  and did nothing. */}
              {/* 🚨 The Carousels field is GONE too, for the same reason.
                  A room type is company-scoped or GLOBAL, so an arrangement
                  authored on a shared "Standard" reached every customer using
                  it. The home page lives on the PREMISE now
                  (`Premise.carousels`, Streaming TV → Carousels) and nothing
                  reads `RoomType.carousels` any more. Sub-pages have NOT moved
                  — they are still layout, which is what a room type is for. */}
            </>
          )}
          {err && (
            <Typography variant="caption" color="error">
              {err}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !name.trim()}
          data-testid="room-type-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
